/* BUSINESS PERFORMANCE — the Pole page (2026-10-26; Max, on YS's 22/10 mail about inventory, sales and purchasing of the
   pole-based products in one view: "làm 1 trang bảng báo cáo bên Business performance … kết hợp cả bảng và chart nhé có
   thêm các tính năng như export excel, html"). One Current position snapshot of the Global Pole Inventory, a morning one
   every day: tiles, four charts, then the tables. Every figure — totals, months of supply — comes from /api/bp/pole
   (bizTables.js), which also builds the Excel and HTML files, so the three cannot disagree. */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, FileCode2, Link2, Copy, X } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { useTheme } from './ThemeContext';
import { SERIES, OTHER_COLOR } from './explorerModel';
import { Section, useFetch, useBzTop, fmtDay, fmtYm, int } from './BizTables';
/* the share dialog wears the EUSU screen's (eu-overlay / eu-modal / pa-btn) */
import './ProjectApproval.css';
import './EusuStock.css';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/bp`;
const ENT_INDEX = { HINC: 0, HUSA: 1, HEMA: 2, HPAC: 3 };
/* stock today and open PO are one quantity at two moments: one blue, dark and light (InventoryReport's ramp) */
const MEASURE = { light: { now: '#1c5cab', po: '#86b6ef' }, dark: { now: '#86b6ef', po: '#184f95' } };
const dec1 = (v) => (v == null || !Number.isFinite(v) ? '-' : v.toFixed(1));
const kShort = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(Math.abs(v) >= 10000 ? 0 : 1)}K` : String(Math.round(v * 10) / 10));
const shortYm = (b) => (/^\d{4}-\d{2}$/.test(b) ? `${fmtYm(b).slice(0, 3)} '${b.slice(2, 4)}` : b);

function ChartCard({ title, legend, children, height = 260 }) {
  return (
    <div className="bz-chart">
      <h4>{title}</h4>
      <div className="bz-legend">
        {legend.map((l) => <span key={l.name}><i style={{ background: l.color }} />{l.name}</span>)}
      </div>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

export function BizPole() {
  const { t } = useTranslation();
  useBzTop();
  const { resolved } = useTheme();
  const mode = resolved === 'dark' ? 'dark' : 'light';
  const [day, setDay] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const { data, error, loading, reload } = useFetch(`${API}/pole${day ? `?day=${day}` : ''}`);
  /* the share link: the button shows only for whoever may manage it (an editor of Global Pole Inventory — the server
     answers 403 to everyone else) */
  const [canShare, setCanShare] = useState(false);
  const [share, setShare] = useState(null); // null = closed; { url, busy, copied, err }
  useEffect(() => {
    let live = true;
    fetch(`${API}/pole/share`, { credentials: 'include' }).then((r) => { if (live) setCanShare(r.ok); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const shareCall = async (method) => {
    setShare((x) => ({ ...(x || {}), busy: true, err: '' }));
    try {
      const r = await fetch(`${API}/pole/share`, { method, credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setShare({ url: j.url, busy: false, copied: false });
    } catch (e) { setShare((x) => ({ ...(x || {}), busy: false, err: e.message })); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(share.url); } catch (e) {
      const ta = document.createElement('textarea'); ta.value = share.url; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
    setShare((x) => ({ ...x, copied: true }));
  };
  const d = data || {};
  const ent = d.byEntity || [];
  const g = d.group;
  const m = d.base ? fmtYm(d.base) : '';
  const colorOf = (e) => SERIES[mode][ENT_INDEX[e] ?? 7];
  const grid = mode === 'dark' ? '#2c2c2a' : '#e8e8e4';
  const tick = { fontSize: 11, fill: mode === 'dark' ? '#a3a3a3' : '#64748b' };
  const bucketName = (b) => (b === 'overdue' ? t('xBiz.poOverdue') : b === 'nodate' ? t('xBiz.poNoDate') : fmtYm(b));
  const tipStyle = { contentStyle: { background: mode === 'dark' ? '#1d1d1d' : '#fff', border: `1px solid ${grid}`, fontSize: 12 }, cursor: { fill: mode === 'dark' ? 'rgba(255,255,255,.04)' : 'rgba(0,0,0,.04)' } };

  /* the movement columns: what there was, what came and went, what there is, what is coming */
  const cols = [
    ['endQty', t('xBiz.endQty', { m })], ['received', t('xBiz.poleReceived')], ['toGroup', t('xBiz.poleToGroup')],
    ['sold', t('xBiz.sold')], ['other', t('xBiz.poleOther')], ['nowQty', t('xBiz.nowQty')], ['avgMonth', t('xBiz.avgMonth')],
    ['mos', t('xBiz.mos')], ['poTotal', t('xBiz.openPo')], ['mosPo', t('xBiz.mosPo')],
  ];
  const cells = (x) => cols.map(([k]) => {
    const v = x[k];
    const isDec = k === 'mos' || k === 'mosPo';
    const warn = k === 'mos' && v != null && v < 2;
    return <td key={k} className={`bz-num${warn ? ' bz-warn' : ''}${k === 'nowQty' || k === 'poTotal' ? ' bz-sep' : ''}`}>{isDec ? dec1(v) : int(v)}</td>;
  });
  const head = (first) => (
    <tr>
      <th className="bz-first">{first}</th>
      {cols.map(([k, label]) => <th key={k} className={k === 'nowQty' || k === 'poTotal' ? 'bz-sep' : ''}>{label}</th>)}
    </tr>
  );
  const toggle = (e) => setOpen((s) => { const n = new Set(s); if (n.has(e)) n.delete(e); else n.add(e); return n; });
  const allOpen = ent.length > 0 && ent.every((x) => open.has(x.entity));

  const stockData = ent.map((x) => ({ name: x.entity, end: Math.round(x.endQty), now: Math.round(x.nowQty), po: Math.round(x.poTotal) }));
  const poData = (d.buckets || []).map((b) => ({ name: shortYm(b === 'overdue' ? t('xBiz.poOverdue') : b === 'nodate' ? t('xBiz.poNoDate') : b), ...Object.fromEntries(ent.map((x) => [x.entity, Math.round(x.po[b] || 0)])) }));
  const top = [...(d.byFamily || [])].sort((a, b) => b.nowQty - a.nowQty).slice(0, 12)
    .map((f) => ({ name: f.family, ...Object.fromEntries((d.entities || []).map((e) => [e, Math.max(0, Math.round(f.nowBy[e] || 0))])) }));
  const mosData = ent.map((x) => ({ name: x.entity, mos: x.mos == null ? 0 : Math.round(x.mos * 10) / 10, mosPo: x.mosPo == null ? 0 : Math.round(x.mosPo * 10) / 10 }));
  const entLegend = (d.entities || []).map((e) => ({ name: e, color: colorOf(e) }));
  const fileHref = (ext) => `${API}/pole.${ext}${d.day ? `?day=${d.day}` : ''}`;

  return (
    <div className="bz-page" data-bz="pole">
      <div className="bz-controls">
        <label className="srd-field">
          <span>{t('xBiz.day')}</span>
          <select value={day || d.day || ''} onChange={(e) => setDay(e.target.value)} disabled={!d.days?.length}>
            {(d.days || []).map((x) => <option key={x.day} value={x.day}>{fmtDay(x.day)} · {t('xBiz.poleBase', { m: fmtYm(x.base) })}</option>)}
          </select>
        </label>
        <button type="button" className="srd-primary bz-reload" onClick={reload} disabled={loading}>{loading ? t('xBiz.loading') : t('xBiz.reload')}</button>
        {d.day && (
          <span className="bz-files">
            <a className="bz-file" href={fileHref('xlsx')} download data-bz-export="xlsx"><FileSpreadsheet size={15} />{t('xBiz.exportXlsx')}</a>
            <a className="bz-file" href={fileHref('html')} download data-bz-export="html"><FileCode2 size={15} />{t('xBiz.exportHtml')}</a>
            {canShare && (
              <button type="button" className="bz-file" data-bz-share onClick={() => { setShare({ url: null, busy: true }); shareCall('GET'); }}>
                <Link2 size={15} />{t('xBiz.shareLink')}
              </button>
            )}
          </span>
        )}
      </div>
      <section className="srd-summary">
        <h2>{t('xBiz.poleTitle')}</h2>
        <p>{d.day ? t('xBiz.poleSubPage', { d: fmtDay(d.day), m, f: fmtDay(d.from), s: d.salesThrough ? fmtDay(d.salesThrough) : '—' }) : ' '}</p>
      </section>
      {error && <div className="srd-message error">{error}</div>}
      {loading && !data && <div className="srd-message">{t('xBiz.loading')}</div>}
      {data && !d.day && <div className="srd-empty">{t('xBiz.poleEmpty')}</div>}

      {g && (
        <div className="bz-tiles" data-bz-tiles="pole">
          {[
            [t('xBiz.endQty', { m }), int(g.endQty)], [t('xBiz.poleReceived'), int(g.received)], [t('xBiz.sold'), int(g.sold)],
            [t('xBiz.nowQty'), int(g.nowQty)], [t('xBiz.openPo'), int(g.poTotal)], [t('xBiz.poleMosTile'), `${dec1(g.mos)} · ${dec1(g.mosPo)}`],
          ].map(([k, v]) => <div key={k} className="bz-tile"><span>{k}</span><b>{v}</b></div>)}
        </div>
      )}

      {g && (
        <div className="bz-charts">
          <ChartCard title={t('xBiz.chStock')} legend={[
            { name: t('xBiz.endQty', { m }), color: OTHER_COLOR[mode] }, { name: t('xBiz.nowQty'), color: MEASURE[mode].now }, { name: t('xBiz.openPo'), color: MEASURE[mode].po }]}>
            <BarChart data={stockData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={2}>
              <CartesianGrid vertical={false} stroke={grid} />
              <XAxis dataKey="name" tick={tick} axisLine={false} tickLine={false} />
              <YAxis tick={tick} tickFormatter={kShort} axisLine={false} tickLine={false} width={44} />
              <Tooltip {...tipStyle} formatter={(v) => int(v)} />
              <Bar dataKey="end" name={t('xBiz.endQty', { m })} fill={OTHER_COLOR[mode]} radius={[4, 4, 0, 0]} maxBarSize={26} />
              <Bar dataKey="now" name={t('xBiz.nowQty')} fill={MEASURE[mode].now} radius={[4, 4, 0, 0]} maxBarSize={26} />
              <Bar dataKey="po" name={t('xBiz.openPo')} fill={MEASURE[mode].po} radius={[4, 4, 0, 0]} maxBarSize={26} />
            </BarChart>
          </ChartCard>
          <ChartCard title={t('xBiz.chPoMonth')} legend={entLegend}>
            <BarChart data={poData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={grid} />
              <XAxis dataKey="name" tick={tick} axisLine={false} tickLine={false} interval={0} />
              <YAxis tick={tick} tickFormatter={kShort} axisLine={false} tickLine={false} width={44} />
              <Tooltip {...tipStyle} formatter={(v) => int(v)} />
              {(d.entities || []).map((e, i, arr) => (
                <Bar key={e} dataKey={e} stackId="po" fill={colorOf(e)} stroke={mode === 'dark' ? '#171717' : '#fff'} strokeWidth={1}
                  radius={i === arr.length - 1 ? [4, 4, 0, 0] : 0} maxBarSize={40} />
              ))}
            </BarChart>
          </ChartCard>
          <ChartCard title={t('xBiz.chTopFam')} legend={entLegend} height={330}>
            <BarChart data={top} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid horizontal={false} stroke={grid} />
              <XAxis type="number" tick={tick} tickFormatter={kShort} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={tick} width={150} axisLine={false} tickLine={false} interval={0} />
              <Tooltip {...tipStyle} formatter={(v) => int(v)} />
              {(d.entities || []).map((e, i, arr) => (
                <Bar key={e} dataKey={e} stackId="f" fill={colorOf(e)} stroke={mode === 'dark' ? '#171717' : '#fff'} strokeWidth={1}
                  radius={i === arr.length - 1 ? [0, 4, 4, 0] : 0} maxBarSize={16} />
              ))}
            </BarChart>
          </ChartCard>
          <ChartCard title={t('xBiz.chMos')} legend={[{ name: t('xBiz.mos'), color: MEASURE[mode].now }, { name: t('xBiz.mosPo'), color: MEASURE[mode].po }]} height={330}>
            <BarChart data={mosData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={2}>
              <CartesianGrid vertical={false} stroke={grid} />
              <XAxis dataKey="name" tick={tick} axisLine={false} tickLine={false} />
              <YAxis tick={tick} axisLine={false} tickLine={false} width={44} />
              <Tooltip {...tipStyle} formatter={(v) => dec1(v)} />
              <Bar dataKey="mos" name={t('xBiz.mos')} fill={MEASURE[mode].now} radius={[4, 4, 0, 0]} maxBarSize={30} />
              <Bar dataKey="mosPo" name={t('xBiz.mosPo')} fill={MEASURE[mode].po} radius={[4, 4, 0, 0]} maxBarSize={30} />
            </BarChart>
          </ChartCard>
        </div>
      )}

      {ent.length > 0 && (
        <Section title={t('xBiz.secEntity')} note={t('xBiz.poleNote', { q: int(d.hincForSubs), m })}>
          <table className="bz-table" data-bz-table="pole-entity">
            <thead>{head(t('xBiz.entity'))}</thead>
            <tbody>
              {ent.map((x) => <tr key={x.entity}><td className="bz-first">{x.entity}</td>{cells(x)}</tr>)}
              <tr className="total"><td className="bz-first">{t('xBiz.total')}</td>{cells(g)}</tr>
            </tbody>
          </table>
        </Section>
      )}

      {ent.length > 0 && (
        <Section
          title={t('xBiz.secPoleFamily')}
          right={<button type="button" className="bz-link" onClick={() => setOpen(allOpen ? new Set() : new Set(ent.map((x) => x.entity)))}>{allOpen ? t('xBiz.collapseAll') : t('xBiz.expandAll')}</button>}
        >
          <table className="bz-table" data-bz-table="pole-family">
            <thead>{head(`${t('xBiz.entity')} / ${t('xBiz.family')}`)}</thead>
            <tbody>
              {ent.map((e) => (
                <React.Fragment key={e.entity}>
                  <tr className="bz-group" onClick={() => toggle(e.entity)} data-bz-entity={e.entity}>
                    <td className="bz-first"><span className="bz-caret">{open.has(e.entity) ? '▾' : '▸'}</span>{e.entity}</td>{cells(e)}
                  </tr>
                  {open.has(e.entity) && d.rows.filter((x) => x.entity === e.entity).map((x) => (
                    <tr key={`${x.entity}|${x.familyCode}`}><td className="bz-first bz-fam">{x.family}</td>{cells(x)}</tr>
                  ))}
                </React.Fragment>
              ))}
              <tr className="total"><td className="bz-first">{t('xBiz.total')}</td>{cells(g)}</tr>
            </tbody>
          </table>
        </Section>
      )}

      {(d.byFamily || []).length > 0 && (
        <Section title={t('xBiz.secPoleByFam')}>
          <table className="bz-table" data-bz-table="pole-byfamily">
            <thead>
              <tr>
                <th className="bz-first">{t('xBiz.family')}</th>
                {d.entities.map((e) => <th key={e}>{t('xBiz.nowQty')} {e}</th>)}
                <th className="bz-sep">{t('xBiz.nowQty')}</th><th>{t('xBiz.openPo')}</th><th>{t('xBiz.avgMonth')}</th>
                <th>{t('xBiz.mos')}</th><th>{t('xBiz.mosPo')}</th>
              </tr>
            </thead>
            <tbody>
              {[...d.byFamily, { ...g, family: t('xBiz.total'), total: true, nowBy: Object.fromEntries(ent.map((x) => [x.entity, x.nowQty])) }].map((x) => (
                <tr key={x.familyCode || '_total'} className={x.total ? 'total' : ''}>
                  <td className="bz-first">{x.family}</td>
                  {d.entities.map((e) => <td key={e} className="bz-num">{int(x.nowBy[e])}</td>)}
                  <td className="bz-num bz-sep">{int(x.nowQty)}</td>
                  <td className="bz-num">{int(x.poTotal)}</td>
                  <td className="bz-num">{int(x.avgMonth)}</td>
                  <td className={`bz-num ${x.mos != null && x.mos < 2 ? 'bz-warn' : ''}`}>{dec1(x.mos)}</td>
                  <td className="bz-num">{dec1(x.mosPo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {ent.length > 0 && (d.buckets || []).length > 0 && (
        <Section title={t('xBiz.chPoMonth')}>
          <table className="bz-table" data-bz-table="pole-po">
            <thead>
              <tr>
                <th className="bz-first">{t('xBiz.entity')}</th>
                {d.buckets.map((b) => <th key={b}>{bucketName(b)}</th>)}
                <th className="bz-sep">{t('xBiz.total')}</th>
              </tr>
            </thead>
            <tbody>
              {[...ent, { ...g, entity: t('xBiz.total'), total: true }].map((x) => (
                <tr key={x.entity} className={x.total ? 'total' : ''}>
                  <td className="bz-first">{x.entity}</td>
                  {d.buckets.map((b) => <td key={b} className={`bz-num ${b === 'overdue' && x.po[b] ? 'bz-warn' : ''}`}>{int(x.po[b] || 0)}</td>)}
                  <td className="bz-num bz-sep">{int(x.poTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {share && (
        <div className="eu-overlay" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) setShare(null); }}>
          <div className="eu-modal eu-share" data-bz-share-dialog>
            <div className="eu-modal-head">
              <b>{t('xBiz.shareTitle')}</b>
              <button type="button" className="eu-x" onClick={() => setShare(null)} aria-label={t('xBiz.close')} title={t('xBiz.close')}><X size={18} /></button>
            </div>
            <p className="eu-sub">{t('xBiz.shareSub')}</p>
            <div className="eu-modal-body">
              {share.url ? (
                <div className="eu-linkrow">
                  <input readOnly value={share.url} onFocus={(e) => e.target.select()} aria-label={t('xBiz.shareLink')} />
                  <button type="button" className="pa-btn primary" onClick={copyLink}><Copy size={14} /> {share.copied ? t('xBiz.copied') : t('xBiz.copy')}</button>
                  <a className="pa-btn" href={share.url} target="_blank" rel="noreferrer">{t('xBiz.open')}</a>
                </div>
              ) : (!share.busy && <p className="eu-nolink">{t('xBiz.noLink')}</p>)}
              <p className="eu-warn">{t('xBiz.shareWarn')}</p>
              {share.err && <div className="eu-flash bad">{share.err}</div>}
            </div>
            <div className="eu-modal-foot">
              {share.url && <button type="button" className="pa-btn" disabled={share.busy} onClick={() => shareCall('DELETE')}>{t('xBiz.switchOff')}</button>}
              <button type="button" className="pa-btn primary" disabled={share.busy} data-bz-share-make onClick={() => shareCall('POST')}>{share.url ? t('xBiz.replaceLink') : t('xBiz.makeLink')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
